import classes from "./StatsBoard.module.css";

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
};

const formatDuration = (ms = 0) => {
  if (!ms || Number.isNaN(ms)) return "--";
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${mins}m ${remainingSeconds}s`;
};

export default function StatsBoard({
  counts = {},
  importSummary = {},
  recentRuns = [],
  generatedAt = "",
  title = "Data Overview",
  subtitle = "Latest catalog stats and import activity.",
}) {
  const lastRun = importSummary?.last_run || null;

  return (
    <div className={classes.wrapper}>
      <header className={classes.header}>
        <div>
          <p className={classes.kicker}>Reporting</p>
          <h2>{title}</h2>
          <p className={classes.subtitle}>{subtitle}</p>
        </div>
        <div className={classes.timestamp}>
          Last updated <strong>{formatDateTime(generatedAt)}</strong>
        </div>
      </header>

      <section className={classes.cards}>
        {[
          { label: "Products", value: counts.products },
          { label: "Categories", value: counts.categories },
          { label: "Brands", value: counts.brands },
        ].map((card) => (
          <article key={card.label} className={classes.card}>
            <p>{card.label}</p>
            <strong>{card.value ?? 0}</strong>
          </article>
        ))}
      </section>

      <section className={classes.summary}>
        <div className={classes.summaryCard}>
          <div className={classes.summaryRow}>
            <span>Total imports</span>
            <strong>{importSummary?.total_runs ?? 0}</strong>
          </div>
          <div className={classes.summaryRow}>
            <span>Rows processed</span>
            <strong>{importSummary?.total_rows ?? 0}</strong>
          </div>
          <div className={classes.summaryRow}>
            <span>Rows imported</span>
            <strong>{importSummary?.total_imported ?? 0}</strong>
          </div>
          <div className={classes.summaryRow}>
            <span>Rows skipped</span>
            <strong>{importSummary?.total_skipped ?? 0}</strong>
          </div>
        </div>
        <div className={classes.lastRunCard}>
          <h3>Latest Import</h3>
          {lastRun ? (
            <>
              <div className={classes.lastRunGrid}>
                <div>
                  <span>Finished</span>
                  <strong>{formatDateTime(lastRun.finished_at)}</strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>{formatDuration(lastRun.duration_ms)}</strong>
                </div>
                <div>
                  <span>Imported / Skipped</span>
                  <strong>
                    {lastRun.rows_imported} / {lastRun.rows_skipped}
                  </strong>
                </div>
                <div>
                  <span>Triggered by</span>
                  <strong>{lastRun.user_name || lastRun.user_email || "—"}</strong>
                </div>
              </div>
              <p className={classes.fileName}>
                File: <strong>{lastRun.file_name || "—"}</strong>
              </p>
            </>
          ) : (
            <p className={classes.emptyState}>No imports recorded yet.</p>
          )}
        </div>
      </section>

      <section className={classes.tableCard}>
        <div className={classes.tableHead}>
          <div>
            <h3>Recent Runs</h3>
            <p>Last {recentRuns.length || 0} entries</p>
          </div>
        </div>
        {recentRuns.length === 0 ? (
          <div className={classes.emptyState}>No activity recorded.</div>
        ) : (
          <div className={classes.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Completed</th>
                  <th>Imported</th>
                  <th>Skipped</th>
                  <th>Duration</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
              {recentRuns.map((run, idx) => (
                <tr key={run.id || run.finished_at || idx}>
                    <td>{formatDateTime(run.finished_at)}</td>
                    <td>{run.rows_imported}</td>
                    <td>{run.rows_skipped}</td>
                    <td>{formatDuration(run.duration_ms)}</td>
                    <td>{run.user_name || run.user_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

